import { Part, Tooth } from '@/storage/gateways/VisitGateway';

const surfaceIds = [
  'surfaces',
  'central_surface',
  'distal_contact_surface',
  'oral_surface',
  'vestibular_surface',
  'medial_contact_surface',
];

const canalsIds = [
  'canals',
  'cheek_canal',
  'distal_cheek_canal',
  'medial_cheek_canal',
  'lingual_canal',
  'central_canal',
  'additional_canal',
  'palatal_canal',
];

const deletedPartIds = [
  'delete',
  'root_deleted',
];

export default {
  SURFACES_PART_NAME: 'surfaces',
  BONE_PART_NAME: 'bone',
  CANALS_PART_NAME: 'canals',
  IMPLANT_PART_NAME: 'implant',
  deleteToothPartId: 'crown',
  deleteDiagnosisId: 'delete',
  data() {
    return {
      isShowMenu: false,
      needDeleted: this.isDeleted,
      selectedPartList: [],
      partSelected: '',
    };
  },
  computed: {
    /**
     * @description Список диагнозов частей зуба для исключения.
     * @type {Object.<string,string[]>}
     */
    excludePartDiagnosis() {
      return {
        root_1: ['root_deleted'],
        gingiva: ['hood_dissection'],
      };
    },
    /**
     * @description Список частей.
     * @type {Part[]}
     */
    partList() {
      const parts = Object.keys(this.excludePartDiagnosis);
      return this.selectedPartList.map(
        (part) => {
          if (parts.includes(part.id)) {
            const excludedDiagnoses = this.excludePartDiagnosis[part.id];
            const excludeFilter = (candidate) => !excludedDiagnoses.includes(
              candidate.diagnosis.id,
            );
            return new Part(part.id, part.name, part.diagnoses.filter(excludeFilter));
          }
          return part;
        },
      );
    },
    partColor: ({ tooth }) => Object.fromEntries(tooth.parts.map(
      (part) => {
        if (part.diagnoses[0]?.id === 'implant') {
          return [part.id, {}.color];
        }
        return [part.id, (part.diagnoses[0] || {}).color];
      },
    )),
    surface() {
      const surface = this.tooth.parts.filter((item) => surfaceIds.includes(item.id));
      if (this.isDeleted) {
        return surface.map((part) => ({ ...part, diagnoses: [] }));
      }
      return surface;
    },
    /**
     * @description Зуб удален.
     * @type {boolean}
     */
    isDeleted() {
      return this.hiddenParts.includes('crown');
    },
    /**
     * @description Есть ли зуб с имплантом.
     * @type {boolean}
     */
    isDiagnosisImplant() {
      return this.tooth.parts.some(
        (part) => part.diagnoses[0]?.id === this.$options.IMPLANT_PART_NAME,
      );
    },
    /**
     * @description Есть ли имплант в кости.
     * @type {boolean}
     */
    isBoneImplant() {
      const bone = this.tooth.parts.find(
        (part) => part.id === this.$options.BONE_PART_NAME,
      );
      return bone.diagnoses[0]?.id === this.$options.IMPLANT_PART_NAME;
    },
    /**
     * @description Есть ли коронка на импланте.
     * @type {boolean}
     */
    hasCrownOnImplant() {
      const crown = this.tooth.parts.find((part) => part.id === 'crown');
      const hasImplant = this.isDiagnosisImplant || this.isBoneImplant;
      const hasCrownDiagnosis = crown?.diagnoses?.some(
        (diagnosis) => diagnosis.id !== 'delete',
      );
      return hasImplant && hasCrownDiagnosis;
    },
    /**
     * @description Есть ли имплант на зубе.
     * @type {boolean}
     */
    hasImplant() {
      return this.isDiagnosisImplant || this.isBoneImplant;
    },
    diagnosisList: ({ diagnoses, partSelected }) => (
      partSelected === 'all'
        ? diagnoses
        : { [partSelected]: diagnoses[partSelected] } || {}
    ),
    /**
     * @description Скрытые части.
     * @type {string[]}
     */
    hiddenParts() {
      return this.tooth.parts.filter(
        (part) => part.diagnoses.filter(
          (diagnosis) => deletedPartIds.includes(diagnosis.id),
        ).length !== 0,
      ).map((part) => part.id);
    },
  },
  methods: {
    /**
     * Проверяем, выбрана ли часть зуба относящаяся к
     * поверхностям
     *
     * @param partId
     * @returns {boolean}
     */
    isSelectedSurface(partId) {
      return surfaceIds.indexOf(partId) > -1;
    },
    /**
     * Проверяем, выбрана ли часть зуба относящаяся к
     * каналам
     *
     * @param partId
     * @returns {boolean}
     */
    isSelectedCanal(partId) {
      return canalsIds.indexOf(partId) > -1;
    },
    isSelected(partId) {
      return this.partSelected === partId || this.selectedParts.includes(partId);
    },
    /**
     * @description Фильтровать по выбранным частям.
     * @param {string} rootId Идентификатор корня.
     * @returns {boolean}
     */
    filterBySelectedDiagnoses(rootId) {
      const selectedPart = this.selectedPartList.find((part) => part.id === rootId);
      if (selectedPart) {
        const roots = selectedPart.diagnoses.filter(
          (candidate) => candidate.value === false && candidate.diagnosis.id === 'root_deleted',
        );
        return roots.length > 0;
      }
      return false;
    },
    /**
     * @description Фильтровать по скрытым частям.
     * @param {string} rootId Идентификатор корня.
     * @returns {boolean}
     */
    filterByHiddenParts(candidate) {
      return !this.hiddenParts.includes(candidate);
    },
    /**
     * @description Показывать часть.
     * @param {string} partId Идентификатор части.
     * @returns {boolean}
     */
    isShowPart(partId) {
      return !this.hiddenParts.includes(partId);
    },
    /**
     * @description Обработать клик на "весь зуб" (all-tooth).
     * Если есть имплант - показываем только коронки.
     * @param {InputEvent} event
     * @returns {void}
     */
    onAllToothClick(event) {
      if (this.hasImplant) {
        this.onClick('crown', event);
      } else {
        this.onClick('all', event);
      }
    },
    /**
     * @description Обработать выбор части зуба.
     * @param {string} partId Идентификатор части зуба.
     * @param {InputEvent}
     * @returns {void}
     */
    onClick(partId, event) {
      if (event.ctrlKey === true) {
        this.$emit('part-selected', this.tooth.number, partId);
        this.isShowMenu = false;
        return;
      }
      this.partSelected = partId;
      this.needDeleted = this.isDeleted;
      const partList = partId === 'all'
        ? this.tooth.parts
        : this.tooth.parts.filter((candidate) => candidate.id === partId);
      this.selectedPartList = partList.map((part) => {
        const partName = this.getPartName(part, partId);
        const diagnosisIds = part.diagnoses.map(({ id }) => id);
        const diagnoses = this.diagnosisList[part.id].map((diagnosis) => ({
          diagnosis,
          value: diagnosisIds.includes(diagnosis.id),
        }));
        return {
          id: part.id,
          name: partName,
          diagnoses,
        };
      });
      this.isShowMenu = true;
    },
    /**
     * Получить имя части
     *
     * @param part
     * @param currentlyId
     * @return {*|string}
     */
    getPartName(part, currentlyId) {
      const isCanal = this.isSelectedCanal(currentlyId);
      const isSurface = this.isSelectedSurface(currentlyId);
      if (isCanal) {
        return 'Каналы';
      }
      if (isSurface) {
        return 'Поверхности';
      }
      return part.name;
    },
    /**
     * @description Обработать изменение диагноза части зуба.
     * @param {string}  partId      Идентификатор части зуба.
     * @param {string}  diagnosisId Идентификатор диагноза.
     * @param {boolean} isSelected  Выбран.
     * @returns {void}
     */
    onChangeDiagnoses(partId, diagnosisId, isSelected) {
      if (
        partId === this.$options.deleteToothPartId
        && diagnosisId === this.$options.deleteDiagnosisId
      ) {
        this.needDeleted = isSelected;
      }
      const part = this.selectedPartList.find((candidate) => candidate.id === partId);
      part.diagnoses.find((candidate) => candidate.diagnosis.id === diagnosisId).value = isSelected;
    },
    /**
     * @description Обработать изменение наличия зуба.
     * @param {boolean} isDeleted Удален.
     * @returns {void}
     */
    onChangeToothDeleted(isDeleted) {
      this.needDeleted = isDeleted;
      const part = this.selectedPartList.find(
        (candidate) => candidate.id === this.$options.deleteToothPartId,
      );
      if (part) {
        part
          .diagnoses
          .find((candidate) => candidate.diagnosis.id === this.$options.deleteDiagnosisId)
          .value = isDeleted;
      }
    },
    /**
     * @description Обработать закрытие формы.
     * @returns {void}
     */
    onCloseMenu() {
      this.isShowMenu = false;
      this.partSelected = '';
      this.needDeleted = this.isDeleted;
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const getSelectedDiagnoses = (part) => part.diagnoses.reduce(
        (diagnoses, candidate) => {
          if (candidate.value) {
            diagnoses.push(candidate.diagnosis);
          }
          return diagnoses;
        },
        [],
      );
      const parts = this.partList.map((part) => new Part(
        part.id,
        part.name,
        getSelectedDiagnoses(part),
      ));
      const find = (list, value) => list.find((candidate) => candidate.id === value);
      if (this.isDeleted !== this.needDeleted) {
        const isNotContains = !find(this.partList, this.$options.deleteToothPartId);
        if (isNotContains) {
          const part = find(this.tooth.parts, this.$options.deleteToothPartId);
          let { diagnoses } = part;
          if (this.needDeleted) {
            const diagnosis = find(this.diagnoses[part.id], this.$options.deleteDiagnosisId);
            diagnoses = [...diagnoses, diagnosis];
          } else {
            diagnoses = diagnoses.filter(
              (candidate) => candidate.id !== this.$options.deleteDiagnosisId,
            );
          }
          parts.push(new Part(part.id, part.name, diagnoses));
        }
      }
      this.onCloseMenu();
      this.$emit('submit', this.tooth.number, parts);
    },
  },
  props: {
    flip: Boolean,
    diagnoses: Object,
    /** @type {Tooth} tooth */
    tooth: { type: Tooth, required: true },
    /**
     * @description Выбранные части зуба.
     * @type {string[]}
     */
    selectedParts: { type: Array, default: () => [] },
    /**
     * @description Деактивировано.
     * @type Boolean
     */
    disabled: { type: Boolean, default: () => false },
  },
};
